import type * as React from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

/**
 * A tray that slides in from the right to show **the thing itself**.
 *
 * The convention: a table lists records, and one control on a row opens the
 * artifact that record is *about* — the email that was sent, the document that
 * was attached — beside the list rather than on a page of its own. The list
 * keeps its place, so a reader can work down it opening one after another,
 * which is what checking a log actually is.
 *
 * It is `Sheet` underneath, which is this app's right-hand drawer and brings
 * the focus trap, Escape and scrim with it. `PreviewTray` is the narrower
 * pattern layered on top: a title, a line of context, and a frame holding
 * something already rendered. Reach for `Sheet` directly when the panel is a
 * *form* or a picker; reach for this when it is a preview of a record.
 *
 * See `design-system/COMPONENTS.md`.
 */

interface PreviewTrayProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What is being previewed — the record's own name, not "Preview". */
  title: string
  /** One line of context under it: who, when, what happened to it. */
  description?: React.ReactNode
  /** Facts about the record, shown above the frame. */
  meta?: React.ReactNode
  children: React.ReactNode
}

export function PreviewTray({
  open,
  onOpenChange,
  title,
  description,
  meta,
  children,
}: PreviewTrayProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl">
        <SheetHeader>
          {/* Padded right so a long subject never runs under the close control. */}
          <div className="pr-10">
            <SheetTitle className="truncate">{title}</SheetTitle>
            {description ? <SheetDescription className="mt-1">{description}</SheetDescription> : null}
          </div>
        </SheetHeader>
        {meta ? (
          <div className="shrink-0 border-b border-line px-6 py-3 text-xs text-ink-muted">
            {meta}
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto bg-surface-sunken p-4">{children}</div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * A rendered document inside the tray, isolated in a sandboxed frame.
 *
 * **`srcDoc` on a sandboxed iframe, never `dangerouslySetInnerHTML`.** This is
 * stored markup built from data people typed — a client's name, an event's
 * name — and injecting it into the app's own document would put whatever it
 * contains inside the session. An empty `sandbox` allows nothing: no scripts,
 * no forms, no navigation, no same-origin access.
 *
 * **It fills the tray and scrolls inside itself**, rather than growing to its
 * content. Growing would need the frame's `scrollHeight`, and `sandbox=""`
 * withholds `contentDocument` for exactly the reason the sandbox is there — so
 * a measuring `onLoad` looks reasonable and silently never runs. Given the
 * choice between a frame that cannot be measured and a sandbox with
 * `allow-same-origin`, the sandbox wins: an email is a document, and reading a
 * document by scrolling it is not a hardship.
 */
export function PreviewTrayDocument({ html, title }: { html: string; title: string }) {
  return (
    <iframe
      title={title}
      srcDoc={html}
      sandbox=""
      className={cn('h-full min-h-[32rem] w-full border border-line bg-white')}
    />
  )
}
